import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { APP_URLS } from '../helpers/url_helper';
import Home from '../pages/Home/Home';
import ProtectedRoute from './ProtectedRoute';

const Login = lazy(() => import('../pages/Login/Login'));
const Signup = lazy(() => import('../pages/Signup/Signup'));
const CompanySignup = lazy(() => import('../pages/Signup/CompanySignup'));
const IndividualSignUp = lazy(() => import('../pages/Signup/IndividualSignUp'));
const CompleteProfile = lazy(() => import('../pages/Signup/CompleteProfile'));
const ForgotPassword = lazy(() => import('../pages/Auth/ForgotPassword'));
const ForgotPasswordOTP = lazy(() => import('../pages/Auth/ForgotPasswordOTP'));
const Resetpassword = lazy(() => import('../pages/Auth/Resetpassword'));
const DashboardLayout = lazy(() => import('../pages/Dashboard/DashboardLayout'));
const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard'));
const Assistant = lazy(() => import('../pages/Other/Assistant'));
const Directassistant = lazy(() => import('../pages/Other/Directassistant'));
const Documents = lazy(() => import('../pages/Documents/Documents'));
const Projects = lazy(() => import('../pages/Projects/Projects'));
const ProjectFormRecords = lazy(() => import('../pages/Projects/ProjectFormRecords'));
const Tasks = lazy(() => import('../pages/Projects/Tasks'));
const TaskDetails = lazy(() => import('../pages/Projects/TaskDetails'));
const AllTasks = lazy(() => import('../pages/Projects/AllTasks'));
const Employees = lazy(() => import('../pages/ManageEmployee/Employees'));
const EmployeeDetails = lazy(() => import('../pages/ManageEmployee/EmployeeDetails'));
const Roles = lazy(() => import('../pages/ManageEmployee/Roles'));
const RolePermission = lazy(() => import('../pages/ManageEmployee/RolePermission'));
const Subscription = lazy(() => import('../pages/Subscription/Subscription'));
const NewSubscription = lazy(() => import('../pages/Signup/newSubscription'));
const CheckoutSession = lazy(() => import('../pages/StripePayment/CheckoutSession'));
const PaymentSuccess = lazy(() => import('../pages/StripePayment/PaymentSuccess'));
const PaymentCancel = lazy(() => import('../pages/StripePayment/PaymentCancel'));
const StandaloneCheckoutSession = lazy(() => import('../pages/StripePayment/StandaloneCheckoutSession'));
const StandalonePaymentSuccess = lazy(() => import('../pages/StripePayment/StandalonePaymentSuccess'));
const StandalonePaymentCancel = lazy(() => import('../pages/StripePayment/StandalonePaymentCancel'));
const Profile = lazy(() => import('../pages/Profile/Profile'));
const Settings = lazy(() => import('../pages/Settings/Settings'));
const DigitalFormManagement = lazy(() => import('../pages/DigitalForms/DigitalFormManagement'));
const UseTemplateBuilder = lazy(() => import('../pages/DigitalForms/UseTemplateBuilder'));
const CompanyFormList = lazy(() => import('../pages/DigitalForms/CompanyFormList'));
const CompanyFormFill = lazy(() => import('../pages/DigitalForms/CompanyFormFill'));
const CompanyFormRecords = lazy(() => import('../pages/DigitalForms/CompanyFormRecords'));
const CompanyFormRecordView = lazy(() => import('../pages/DigitalForms/CompanyFormRecordView'));
const AssetRecordList = lazy(() => import('../pages/AssetRecords/AssetRecordList'));
const SitesList = lazy(() => import('../pages/Sites/SitesList'));
const SiteDetail = lazy(() => import('../pages/Sites/SiteDetail'));
const AssetRecordForm = lazy(() => import('../pages/AssetRecords/AssetRecordForm'));
const AssetRecordDetail = lazy(() => import('../pages/AssetRecords/AssetRecordDetail'));
const TermsAndConditions = lazy(() => import('../pages/TermsAndPolicy/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('../pages/TermsAndPolicy/PrivacyPolicy'));
const ContactUs = lazy(() => import('../pages/ContactUs/ContactUs'));
const Enquiry = lazy(() => import('../pages/ContactUs/Enquiry'));
const StandaloneContactSales = lazy(() => import('../pages/ContactUs/StandaloneContactSales'));
const ContactSales = lazy(() => import('../pages/ContactUs/ContactSales'));
const VideoTutorial = lazy(() => import('../pages/Home/VideoTutorial'));
const WHSComplianceSoftware = lazy(() => import('../pages/SeoPages/WHSComplianceSoftware'));
const SafetyManagementSoftware = lazy(() => import('../pages/SeoPages/SafetyManagementSoftware'));
const IncidentManagementSoftware = lazy(() => import('../pages/SeoPages/IncidentManagementSoftware'));
const ContractorManagementSoftware = lazy(() => import('../pages/SeoPages/ContractorManagementSoftware'));
const ConstructionSafetyComplianceSoftware = lazy(() => import('../pages/SeoPages/ConstructionSafetyComplianceSoftware'));
const WHSComplianceAuditSoftware = lazy(() => import('../pages/SeoPages/WHSComplianceAuditSoftware'));
const AIComplianceAssistant = lazy(() => import('../pages/SeoPages/AIComplianceAssistant'));
const DigitalWHSForms = lazy(() => import('../pages/SeoPages/DigitalWHSForms'));
const PlantEquipmentRegisterSoftware = lazy(() => import('../pages/SeoPages/PlantEquipmentRegisterSoftware'));
const SWMSPCBUComplianceSoftware = lazy(() => import('../pages/SeoPages/SWMSPCBUComplianceSoftware'));
const CertificateOfRecognition = lazy(() => import('../pages/SeoPages/CertificateOfRecognition'));

const RouteFallback = () => (
  <div style={{ minHeight: '100vh' }} aria-busy="true" />
);

const AppRoutes = () => (
  <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path={APP_URLS.HOME} element={<Home />} />
      <Route path="/whs-compliance-software" element={<WHSComplianceSoftware />} />
      <Route path="/whs-compliance-software/" element={<WHSComplianceSoftware />} />
      <Route path="/safety-management-software" element={<SafetyManagementSoftware />} />
      <Route path="/safety-management-software/" element={<SafetyManagementSoftware />} />
      <Route path="/incident-management-software" element={<IncidentManagementSoftware />} />
      <Route path="/incident-management-software/" element={<IncidentManagementSoftware />} />
      <Route path="/contractor-management" element={<ContractorManagementSoftware />} />
      <Route path="/contractor-management/" element={<ContractorManagementSoftware />} />
      <Route path="/construction-safety-compliance" element={<ConstructionSafetyComplianceSoftware />} />
      <Route path="/construction-safety-compliance/" element={<ConstructionSafetyComplianceSoftware />} />
      <Route path="/whs-compliance-audit-software" element={<WHSComplianceAuditSoftware />} />
      <Route path="/whs-compliance-audit-software/" element={<WHSComplianceAuditSoftware />} />
      <Route path="/ai-compliance-assistant" element={<AIComplianceAssistant />} />
      <Route path="/ai-compliance-assistant/" element={<AIComplianceAssistant />} />
      <Route path="/digital-whs-forms" element={<DigitalWHSForms />} />
      <Route path="/digital-whs-forms/" element={<DigitalWHSForms />} />
      <Route path="/plant-equipment-register-software" element={<PlantEquipmentRegisterSoftware />} />
      <Route path="/plant-equipment-register-software/" element={<PlantEquipmentRegisterSoftware />} />
      <Route path="/swms-pcbu-compliance" element={<SWMSPCBUComplianceSoftware />} />
      <Route path="/swms-pcbu-compliance/" element={<SWMSPCBUComplianceSoftware />} />
      <Route path="/certificate-of-recognition" element={<CertificateOfRecognition />} />
      <Route path="/certificate-of-recognition/" element={<CertificateOfRecognition />} />
      <Route path={APP_URLS.LOGIN} element={<Login />} />
      <Route path={APP_URLS.SIGNUP} element={<Signup />} />
      <Route path="/directassistant" element={<Directassistant />} />
      <Route path="/company-signup" element={<CompanySignup />} />
      <Route path="/individual-signup" element={<IndividualSignUp />} />
      <Route path="/newSubscription" element={<NewSubscription />} />
      <Route path={APP_URLS.STANDALONE_CONTACT_SALES} element={<StandaloneContactSales />} />
      <Route path="/standalone-checkout" element={<StandaloneCheckoutSession />} />
      <Route path="/standalone-payment-success" element={<StandalonePaymentSuccess />} />
      <Route path="/standalone-payment-cancel" element={<StandalonePaymentCancel />} />
      <Route path={APP_URLS.FORGOT_PASSWORD} element={<ForgotPassword />} />
      <Route path={APP_URLS.FORGOT_PASSWORD_OTP} element={<ForgotPasswordOTP />} />
      <Route path={APP_URLS.RESET_PASSWORD} element={<Resetpassword />} />
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="/enquiry" element={<Enquiry />} />
      <Route path="/learning-hub" element={<VideoTutorial />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/complete-profile" element={<CompleteProfile />} />
        <Route path={APP_URLS.DASHBOARD} element={<DashboardLayout />}>
          <Route index element={<Dashboard />} />
        </Route>
        <Route path={APP_URLS.ASSISTANT} element={<DashboardLayout />}>
          <Route index element={<Assistant />} />
        </Route>
        <Route path={APP_URLS.PROJECTS} element={<DashboardLayout />}>
          <Route index element={<Projects />} />
        </Route>
        <Route path={APP_URLS.PROJECT_FORM_RECORDS} element={<DashboardLayout />}>
          <Route index element={<ProjectFormRecords />} />
        </Route>
        <Route path="/manage-task" element={<DashboardLayout />}>
          <Route index element={<AllTasks />} />
        </Route>
        <Route path={APP_URLS.SITES} element={<DashboardLayout />}>
          <Route index element={<SitesList />} />
        </Route>
        <Route path={APP_URLS.SITE_DETAIL} element={<DashboardLayout />}>
          <Route index element={<SiteDetail />} />
        </Route>
        <Route path="/project/:projectId/tasks" element={<DashboardLayout />}>
          <Route index element={<Tasks />} />
        </Route>
        <Route path="/project/:projectId/task/:taskId" element={<DashboardLayout />}>
          <Route index element={<TaskDetails />} />
        </Route>
        <Route path={APP_URLS.DOCUMENTS} element={<DashboardLayout />}>
          <Route index element={<Documents />} />
        </Route>
        <Route path={APP_URLS.EMPLOYEES} element={<DashboardLayout />}>
          <Route index element={<Employees />} />
        </Route>
        <Route path={APP_URLS.EMPLOYEE_DETAILS} element={<DashboardLayout />}>
          <Route index element={<EmployeeDetails />} />
        </Route>
        <Route path={APP_URLS.ROLES} element={<DashboardLayout />}>
          <Route index element={<Roles />} />
        </Route>
        <Route path={APP_URLS.ROLE_PERMISSION} element={<DashboardLayout />}>
          <Route index element={<RolePermission />} />
        </Route>
        <Route path={APP_URLS.SUBSCRIPTION} element={<DashboardLayout />}>
          <Route index element={<Subscription />} />
        </Route>
        <Route path={APP_URLS.CONTACT_SALES} element={<DashboardLayout />}>
          <Route index element={<ContactSales />} />
        </Route>
        <Route path={APP_URLS.PROFILE} element={<DashboardLayout />}>
          <Route index element={<Profile />} />
        </Route>
        <Route path="/settings" element={<DashboardLayout />}>
          <Route index element={<Settings />} />
        </Route>
        <Route path={APP_URLS.DIGITAL_FORM_MANAGEMENT} element={<DashboardLayout />}>
          <Route index element={<DigitalFormManagement />} />
        </Route>
        <Route path={APP_URLS.DIGITAL_FORM_USE_TEMPLATE} element={<DashboardLayout />}>
          <Route index element={<UseTemplateBuilder />} />
        </Route>
        <Route path={APP_URLS.COMPANY_FORMS} element={<DashboardLayout />}>
          <Route index element={<CompanyFormList />} />
        </Route>
        <Route path={APP_URLS.COMPANY_FORM_FILL} element={<DashboardLayout />}>
          <Route index element={<CompanyFormFill />} />
        </Route>
        <Route path={APP_URLS.COMPANY_FORM_RECORDS} element={<DashboardLayout />}>
          <Route index element={<CompanyFormRecords />} />
        </Route>
        <Route path={APP_URLS.COMPANY_FORM_RECORD_VIEW} element={<DashboardLayout />}>
          <Route index element={<CompanyFormRecordView />} />
        </Route>
        <Route path={APP_URLS.ASSET_RECORD} element={<DashboardLayout />}>
          <Route index element={<AssetRecordList />} />
        </Route>
        <Route path={APP_URLS.ASSET_RECORD_NEW} element={<DashboardLayout />}>
          <Route index element={<AssetRecordForm />} />
        </Route>
        <Route path={APP_URLS.ASSET_RECORD_EDIT} element={<DashboardLayout />}>
          <Route index element={<AssetRecordForm />} />
        </Route>
        <Route path={APP_URLS.ASSET_RECORD_DETAIL} element={<DashboardLayout />}>
          <Route index element={<AssetRecordDetail />} />
        </Route>
        <Route path={APP_URLS.CHECKOUT} element={<DashboardLayout />}>
          <Route index element={<CheckoutSession />} />
        </Route>
        <Route path={APP_URLS.PAYMENT_SUCCESS} element={<DashboardLayout />}>
          <Route index element={<PaymentSuccess />} />
        </Route>
        <Route path={APP_URLS.PAYMENT_CANCEL} element={<DashboardLayout />}>
          <Route index element={<PaymentCancel />} />
        </Route>
      </Route>
    </Routes>
  </Suspense>
);

export default AppRoutes;
